import {
  Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, inject, signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ForecastService, ForecastPoint } from '../../../core/services/forecast.service';
import { AjustementManuelService, HistoricalPeriod } from '../../ajustement-manuel/ajustement-manuel.service';
import { SensorHistoricalService, SensorHistoricalPoint } from '../../../core/services/sensor-historical.service';
import { TimezoneService } from '../../../core/services/timezone.service';
import { formatInTimeZone } from 'date-fns-tz';
import { Subscription, interval, startWith, of, map, switchMap, Subject, takeUntil, debounceTime, combineLatest } from 'rxjs';

type SlotData = { slot: number; minutes: number; open: boolean; label: string };

@Component({
  selector: 'app-forecast-graph',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './forecast-graph.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./forecast-graph.component.scss'],
})
export class ForecastGraphComponent implements OnInit, OnChanges, OnDestroy {
  /** File à afficher */
  @Input() queueId: string | null | undefined;

  /** Si true => journée entière (24 points horaires). Sinon => horizonMinutes rolling. */
  @Input() fullDay = true;

  /** Horizon de projection (minutes) si fullDay=false */
  @Input() horizonMinutes = 60;

  /** Mode "nu" (sans cadre) */
  @Input() bare = false;

  /** Seuils (personnalisables) */
  @Input() lowThreshold = 15;
  @Input() highThreshold = 30;

  /** Couleurs des barres (personnalisables) — défaut inchangé pour les usages existants */
  @Input() barColorLow = '#22c55e';
  @Input() barColorMid = '#f59e0b';
  @Input() barColorHigh = '#ef4444';

  /** Jour de la semaine (1=lundi, 7=dimanche) */
  @Input() dayOfWeek?: number;

  /**
   * Plafond visuel facultatif :
   * - null/undefined => auto-scale dynamique basé sur les données
   * - nombre => plafond fixe (comportement précédent)
   */
  @Input() maxY: number | null = null;

  /** Données personnalisées pour remplacer les données de l'API */
  @Input() customData?: {time: string, minutes: number}[];

  /** Charger aussi la baseline */
  @Input() loadBaseline = false;

  /** Afficher la baseline (si chargée) */
  @Input() displayBaseline = true;

  /** Afficher les scans historiques */
  @Input() showHistoricalScans = false;

  /** Charger les scans historiques depuis l'API */
  @Input() loadHistoricalScans = false;

  /** Afficher les valeurs sur les barres */
  @Input() showValues = false;

  /** Période sélectionnée */
  @Input() selectedPeriod?: string;

  /** Nom de la queue */
  @Input() queueName?: string;

  /** Nom du contexte/situation */
  @Input() contextName?: string;

  /** Données des scans historiques enrichies */
  @Input() historicalScansDetails?: any;

  /** Afficher la courbe flux capteur */
  @Input() showSensorFlow = false;

  /** Charger les données flux capteur depuis l'API */
  @Input() loadSensorFlow = false;

  /** Points capteur fournis directement par le parent (prioritaire sur loadSensorFlow) */
  @Input() set externalSensorFlowPoints(pts: SensorHistoricalPoint[] | undefined) {
    if (!pts || pts.length === 0) return;
    // Mapping positionnel : pts[i] correspond au créneau i (même logique que le bloc du bas)
    this.sensorFlowData = this.timeSlots.map((_, i) => {
      const p = pts[i];
      return p ? { avgIn: p.avgIn, avgOut: p.avgOut, occurrenceCount: p.occurrenceCount } : { avgIn: 0, avgOut: 0, occurrenceCount: 0 };
    });
    // Utilise la présence cumulée calculée par le backend si disponible, sinon recalcul local
    if (pts[0]?.cumulativeOccupancy !== undefined) {
      this.sensorOccData = this.timeSlots.map((_, i) =>
        Math.round((pts[i]?.cumulativeOccupancy ?? 0) * 10) / 10
      );
    } else {
      let occ = 0;
      this.sensorOccData = this.sensorFlowData.map(d => {
        occ = Math.max(0, occ + d.avgIn - d.avgOut);
        return Math.round(occ * 10) / 10;
      });
    }
  }

  private translate = inject(TranslateService);
  private api = inject(ForecastService);
  private ajustementApi = inject(AjustementManuelService);
  private sensorApi = inject(SensorHistoricalService);
  private timezone = inject(TimezoneService);

  loading = signal(true);
  points = signal<ForecastPoint[]>([]);
  fullApiResponse = signal<any>(null);
  updating = signal(false);

  // Seuils exposés (signals pour template)
  lowTh = signal<number>(15);
  highTh = signal<number>(30);

  private sub?: Subscription;
  private destroy$ = new Subject<void>();
  private restart$ = new Subject<void>(); // déclencheur sur changement d'input

  /**
   * Cache court (même durée que le polling live) des réponses forecast déjà récupérées,
   * pour éviter de refaire l'appel réseau quand on revient sur un jour déjà consulté
   * (ex: onglets Lun/Mar/Mer.../Dim dans "Prévisions").
   */
  private readonly forecastCache = new Map<string, { points: ForecastPoint[]; fullResponse: any; baseline: any; fetchedAt: number }>();
  private readonly FORECAST_CACHE_TTL_MS = 60_000;

  private forecastCacheKey(): string {
    return `${this.queueId}:${this.fullDay ? 'full' : 'horizon'}:${this.dayOfWeek ?? ''}:${this.horizonMinutes ?? ''}`;
  }

  /** Méthode publique pour forcer le rechargement */
  forceReload() {
    this.cachedTooltipData = null;
    this.cachedTooltipIndex = null;
    this.historicalScansData = undefined;
    this.historicalScansResponse = undefined;
    this.sensorFlowData = undefined;
    this.forecastCache.clear();
    this.restart$.next();
    if (this.loadHistoricalScans) {
      this.loadHistoricalScansData();
    }
    if (this.loadSensorFlow) {
      this.loadSensorFlowData();
    }
  }

  // propriétés utilitaires pour le SVG
  W = 1000;   // largeur viewBox
  H = 300;    // hauteur viewBox
  axisH = 24; // zone basse pour les labels X
  gap = 6.25; // espace entre barres (~20% de la largeur de colonne, comme la démo)
  timeSlots = Array.from({ length: 32 }, (_, i) => {
    const totalMinutes = 6 * 60 + i * 30; // Commence à 6h, +30min à chaque fois
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    // Heure paddée sur 2 chiffres ("07:30" pas "7:30") : indispensable pour matcher
    // response.points[].time (format backend "HH:mm" toujours paddé) dans
    // loadHistoricalScansData/loadSensorFlowData — sans ça, toutes les heures < 10h
    // ne matchaient jamais et leurs données retombaient silencieusement à 0.
    return { hours, minutes, label: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}` };
  }); // 6:00 à 21:30 (32 créneaux)
  colW = this.W / 32;

  /** Plafond auto par défaut (remplacé dès qu'on a des données) */
  private maxYAuto = 10;

  /** Conversion minutes -> Y (px, origine en haut) */
  yToPx = (minutes: number) => {
    const cap = Math.max(0, Math.min(minutes ?? 0, this.maxYEffective()));
    const hPlot = this.H - this.axisH; // hauteur utile
    return hPlot - (cap / this.maxYEffective()) * hPlot;
  };

  /** Créneaux de 30min pour le rendu */
  timeSlotData: SlotData[] = [];

  /** Créneaux baseline pour la courbe pointillée */
  baselineSlotData: SlotData[] = [];
  baselineData: any[] = [];

  /** Ticks Y (dynamiques) */
  yTicks: number[] = [];

  /** Helper pour le template: évite l'usage direct de Math dans le HTML */
  clampTickY = (value: number) => {
    const y = this.yToPx(value);
    return y + 12 < 12 ? 12 : y + 12; // anti-clipping du tick supérieur
  };

  /** Plafond effectif (fixe si fourni, sinon auto) */
  private maxYEffective(): number {
    return (this.maxY ?? this.maxYAuto) || 10;
  }

  /** Détermine si le créneau correspond à l'heure actuelle */
  isCurrentTimeSlot(slotIndex: number): boolean {
    const tz = this.timezone.getEstablishmentTimezone();
    const now = new Date();
    const currentHour = parseInt(formatInTimeZone(now, tz, 'HH'));
    const currentMinute = parseInt(formatInTimeZone(now, tz, 'mm'));
    
    // Convertir l'heure actuelle en minutes depuis 6h
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    const currentSlotMinutes = currentTotalMinutes - 6 * 60; // Offset depuis 6h
    const currentSlotIndex = Math.floor(currentSlotMinutes / 30);

    return slotIndex === currentSlotIndex && currentSlotIndex >= 0 && currentSlotIndex < 32;
  }

  private rebuildTimeSlots() {
    const raw = this.points(); // { time:'HH:mm', minutes:number }[]
    if (!raw || raw.length === 0) {
      this.timeSlotData = this.timeSlots.map((slot, i) => ({ slot: i, minutes: 0, open: false, label: slot.label }));
      this.rebuildScaleFromData();
      return;
    }

    // Mapper les données brutes vers les créneaux de 30min
    const bySlot: Record<number, number[]> = {};
    for (let i = 0; i < 32; i++) bySlot[i] = [];

    raw.forEach(p => {
      const timeMatch = (p.time ?? '00:00').match(/^(\d{1,2}):(\d{2})$/);
      if (timeMatch) {
        const h = parseInt(timeMatch[1], 10);
        const m = parseInt(timeMatch[2], 10);
        if (!Number.isNaN(h) && !Number.isNaN(m)) {
          // Convertir en minutes depuis 6h
          const totalMinutes = h * 60 + m;
          const slotMinutes = totalMinutes - 6 * 60; // Offset depuis 6h
          const slotIndex = Math.floor(slotMinutes / 30);

          if (slotIndex >= 0 && slotIndex < 32) {
            bySlot[slotIndex].push(p.minutes ?? 0);
          }
        }
      }
    });

    this.timeSlotData = this.timeSlots.map((slot, i) => {
      const arr = bySlot[i];
      const val = arr.length ? Math.max(...arr) : 0;
      const isOpen = arr.length > 0; // Le créneau est ouvert s'il y a des données
      // Si val est 0 mais le créneau est ouvert, on met 1 pour afficher une mini barre
      const displayVal = (val === 0 && isOpen) ? 1 : val;
      return { slot: i, minutes: displayVal, open: isOpen, label: slot.label };
    });

    this.rebuildScaleFromData();
  }

  /** Construit ticks & plafond auto à partir des données (si maxY n'est pas forcé) */
  private rebuildScaleFromData() {
    // Si maxY est forcé -> ticks "jolis" basés sur ce plafond
    if (typeof this.maxY === 'number' && !Number.isNaN(this.maxY)) {
      const stepForced = this.chooseNiceStep(this.maxY);
      this.yTicks = this.buildTicks(this.maxY, stepForced);
      return;
    }

    // Auto: on regarde le max des minutes
    const dataMax = Math.max(0, ...this.timeSlotData.map(h => h.minutes ?? 0));
    const base = dataMax > 0 ? dataMax : Math.max(this.highTh(), 10);

    // Plafond choisi comme prochain palier "propre" STRICTEMENT supérieur au max
    const top = this.nextNiceTopAbove(base);

    const step = this.chooseNiceStep(top);

    this.maxYAuto = top;
    this.yTicks = this.buildTicks(top, step);
  }

  /** Palier supérieur "propre" strictement > value */
  private nextNiceTopAbove(value: number): number {
    const tops = [10, 15, 20, 25, 30, 40, 50, 60, 75, 90, 120, 150, 180, 240];
    const found = tops.find(t => t > value);
    if (found) return found;

    // si on dépasse nos tops, on arrondit au multiple de 30 strictement au-dessus
    const step = 30;
    return Math.floor(value / step) * step + step;
  }

  /** Choisit un pas "propre" pour obtenir ~4–6 ticks */
  private chooseNiceStep(maxVal: number): number {
    const targetTickCount = 5; // total ~5 (0 + 4 paliers)
    const rough = Math.max(1, maxVal / targetTickCount);
    const niceSteps = [1, 2, 5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 90];
    const cand = niceSteps.find(s => s >= rough);
    return cand ?? niceSteps[niceSteps.length - 1];
  }

  /** Construit les ticks de 0 au plafond inclus */
  private buildTicks(top: number, step: number): number[] {
    const ticks: number[] = [];
    for (let v = 0; v <= top + 0.0001; v += step) ticks.push(Math.round(v));
    if (ticks[0] !== 0) ticks.unshift(0);
    return ticks;
  }

  private rebuildBaselineSlots() {
    if (!this.baselineData || this.baselineData.length === 0) {
      this.baselineSlotData = [];
      return;
    }

    const baselineBySlot: Record<number, number[]> = {};
    for (let i = 0; i < 32; i++) baselineBySlot[i] = [];

    this.baselineData.forEach(p => {
      const timeMatch = (p.time ?? '00:00').match(/^(\d{1,2}):(\d{2})$/);
      if (timeMatch) {
        const h = parseInt(timeMatch[1], 10);
        const m = parseInt(timeMatch[2], 10);
        if (!Number.isNaN(h) && !Number.isNaN(m)) {
          const totalMinutes = h * 60 + m;
          const slotMinutes = totalMinutes - 6 * 60;
          const slotIndex = Math.floor(slotMinutes / 30);
          if (slotIndex >= 0 && slotIndex < 32) {
            baselineBySlot[slotIndex].push(p.minutes ?? 0);
          }
        }
      }
    });

    this.baselineSlotData = this.timeSlots.map((slot, i) => {
      const arr = baselineBySlot[i];
      const val = arr.length ? Math.max(...arr) : 0;
      return { slot: i, minutes: val, open: arr.length > 0, label: slot.label };
    });
  }

  getBaselinePath(): string {
    if (this.baselineSlotData.length === 0) return '';
    
    const points = this.baselineSlotData.map((slot, i) => {
      const x = (i + 0.5) * this.colW;
      const y = this.yToPx(slot.minutes);
      return `${x},${y}`;
    }).join(' L');
    
    return `M${points}`;
  }

  getHistoricalScansPath(): string {
    if (!this.historicalScansData || this.historicalScansData.length === 0) return '';

    const points = this.historicalScansData.map((avgScans, i) => {
      const x = (i + 0.5) * this.colW;
      const y = this.yHistoricalScans(avgScans);
      return `${x},${y}`;
    }).join(' L');

    return `M${points}`;
  }

  /**
   * Échelle Y dédiée aux scans historiques (nombre de scans QR par créneau,
   * typiquement 0-5) — yToPx() est calibrée sur les minutes d'attente
   * (0-30+), donc réutiliser yToPx() écrasait la courbe tout en bas du
   * graphique (quasi invisible). Même principe que ySensor() pour le flux
   * capteur : normalisation sur le propre max de la série.
   *
   * Une normalisation stricte sur le max (pic = 100% de la hauteur) fait
   * toucher le haut du graphe au moindre pic, même pour 2-3 scans — ça
   * écrase visuellement la courbe contre les barres de temps d'attente.
   * On garde donc une marge : le pic n'atteint que ~65% de la hauteur.
   */
  private static readonly HISTORICAL_SCANS_PEAK_RATIO = 0.65;

  private yHistoricalScans(value: number): number {
    const dataMax = Math.max(1, ...(this.historicalScansData ?? []));
    const max = dataMax / ForecastGraphComponent.HISTORICAL_SCANS_PEAK_RATIO;
    const hPlot = this.H - this.axisH;
    return hPlot - (value / max) * hPlot;
  }

  getHistoricalScanY(index: number): number {
    return this.yHistoricalScans(this.getHistoricalScanValue(index));
  }

  private historicalScansData?: number[];
  private historicalScansResponse?: any;
  sensorFlowData?: { avgIn: number; avgOut: number; occurrenceCount: number }[];
  private sensorOccData: number[] = [];
  private cachedTooltipData: any = null;
  private cachedTooltipIndex: number | null = null;

  /** Convertit un avgIn (personnes) en Y pixel via une échelle normalisée propre au capteur */
  private ySensor(value: number): number {
    // Max de la présence cumulée
    const maxOcc = Math.max(1, ...(this.sensorOccData ?? []));
    // Max des autres données du graphe (temps d'attente en minutes)
    const maxForecast = Math.max(1, ...this.points().map(p => p.minutes));
    // Utiliser le max le plus grand pour que la courbe ne sorte pas
    const max = Math.max(maxOcc, maxForecast);
    const hPlot = this.H - this.axisH;
    return hPlot - (value / max) * hPlot;
  }

  private loadHistoricalScansData() {
    if (!this.queueId || !this.loadHistoricalScans || this.customData !== undefined) return;

    const dayOfWeek = this.dayOfWeek ?? this.timezone.getDateInEstablishmentTimezone().getDay();

    this.ajustementApi.getHistoricalScans(this.queueId, dayOfWeek, this.selectedPeriod)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.historicalScansResponse = response;
          // Mapper les points vers les 32 créneaux
          this.historicalScansData = this.timeSlots.map(slot => {
            const point = response.points.find(p => p.time === slot.label);
            return point ? point.avgScans : 0;
          });
        },
        error: (err) => {
          console.error('Failed to load historical scans:', err);
          this.historicalScansData = [];
        }
      });
  }

  getSensorFlowPath(): string {
    if (!this.sensorOccData || this.sensorOccData.length === 0) return '';
    return 'M' + this.sensorOccData.map((occ, i) => {
      const x = (i + 0.5) * this.colW;
      const y = this.ySensor(occ);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' L');
  }

  getSensorFlowValue(index: number): number {
    return this.sensorOccData?.[index] ?? 0;
  }

  getSensorFlowY(index: number): number {
    return this.ySensor(this.getSensorFlowValue(index));
  }

  private _sensorFlowInitialized = false;

  private loadSensorFlowData() {
    if (!this.queueId || !this.loadSensorFlow) return;

    const dayOfWeek = this.dayOfWeek ?? this.timezone.getDateInEstablishmentTimezone().getDay();

    this.sensorApi.getSensorHistorical(this.queueId, dayOfWeek)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.sensorFlowData = this.timeSlots.map(slot => {
            const point = response.points.find(p => p.time === slot.label);
            return point
              ? { avgIn: point.avgIn, avgOut: point.avgOut, occurrenceCount: point.occurrenceCount }
              : { avgIn: 0, avgOut: 0, occurrenceCount: 0 };
          });
        },
        error: () => { this.sensorFlowData = []; }
      });
  }
  hoveredSlot = signal<number | null>(null);
  pinnedSlot = signal<number | null>(null);
  tooltipX = signal(0);
  tooltipY = signal(0);

  hoveredSensorSlot = signal<number | null>(null);
  pinnedSensorSlot  = signal<number | null>(null);
  sensorTooltipX    = signal(0);
  sensorTooltipY    = signal(0);

  getHistoricalScanValue(index: number): number {
    if (!this.historicalScansData) {
      this.getHistoricalScansPath(); // Initialise les données
    }
    return this.historicalScansData?.[index] ?? 0;
  }

  onPointHover(index: number, event: MouseEvent): void {
    if (this.pinnedSlot() === null) {
      this.cachedTooltipIndex = null;
      this.hoveredSlot.set(index);
      const rect = (event.target as SVGElement).getBoundingClientRect();
      this.tooltipX.set(rect.left + rect.width / 2);
      this.tooltipY.set(rect.top - 10);
    }
  }

  onPointLeave(): void {
    if (this.pinnedSlot() === null) {
      this.hoveredSlot.set(null);
    }
  }

  onPointClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedSlot() === index) {
      this.pinnedSlot.set(null);
      this.hoveredSlot.set(null);
      this.cachedTooltipIndex = null;
    } else {
      this.cachedTooltipIndex = null;
      this.pinnedSlot.set(index);
      this.hoveredSlot.set(index);
      const rect = (event.target as SVGElement).getBoundingClientRect();
      this.tooltipX.set(rect.left + rect.width / 2);
      this.tooltipY.set(rect.top - 10);
    }
  }

  closeTooltip(): void {
    this.pinnedSlot.set(null);
    this.hoveredSlot.set(null);
    this.cachedTooltipIndex = null;
  }

  onSensorHover(index: number, event: MouseEvent): void {
    if (this.pinnedSensorSlot() === null) {
      this.hoveredSensorSlot.set(index);
      const rect = (event.target as SVGElement).getBoundingClientRect();
      this.sensorTooltipX.set(rect.left + rect.width / 2);
      this.sensorTooltipY.set(rect.top - 10);
    }
  }

  onSensorLeave(): void {
    if (this.pinnedSensorSlot() === null) {
      this.hoveredSensorSlot.set(null);
    }
  }

  onSensorClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedSensorSlot() === index) {
      this.pinnedSensorSlot.set(null);
      this.hoveredSensorSlot.set(null);
    } else {
      this.pinnedSensorSlot.set(index);
      this.hoveredSensorSlot.set(index);
      const rect = (event.target as SVGElement).getBoundingClientRect();
      this.sensorTooltipX.set(rect.left + rect.width / 2);
      this.sensorTooltipY.set(rect.top - 10);
    }
  }

  closeSensorTooltip(): void {
    this.pinnedSensorSlot.set(null);
    this.hoveredSensorSlot.set(null);
  }

  /** dayOfWeek suit la convention JS Date.getDay() (0=dimanche). */
  private readonly DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  private longDayName(dayOfWeek: number | undefined): string {
    if (!dayOfWeek) return this.translate.instant('previsions.forecastGraph.tooltip.today');
    const code = this.DAY_CODES[dayOfWeek % 7];
    return this.translate.instant('common.days.longByCode.' + code);
  }

  getSensorTooltipData(index: number): { time: string; avgIn: number; avgOut: number; occurrenceCount: number; occ: number } {
    const slot = this.timeSlots[index];
    const end = this.timeSlots[index + 1]?.label ?? '22:00';
    const d = this.sensorFlowData?.[index];
    const dayName = this.longDayName(this.dayOfWeek);
    return {
      time: `${dayName} ${slot.label} – ${end}`,
      avgIn: d ? Math.round(d.avgIn * 10) / 10 : 0,
      avgOut: d ? Math.round(d.avgOut * 10) / 10 : 0,
      occurrenceCount: d?.occurrenceCount ?? 0,
      occ: this.sensorOccData[index] ?? 0,
    };
  }

  getTooltipData(index: number) {
    if (this.cachedTooltipIndex === index && this.cachedTooltipData) {
      return this.cachedTooltipData;
    }

    const slot = this.timeSlots[index];
    const scansMoyen = this.getHistoricalScanValue(index);
    const dayName = this.longDayName(this.dayOfWeek);
    
    const nextTotalMin = 6 * 60 + (index + 1) * 30;
    const endH = Math.floor(nextTotalMin / 60);
    const endM = nextTotalMin % 60;
    const endLabel = this.timeSlots[index + 1]?.label ?? `${endH}:${String(endM).padStart(2, '0')}`;
    const timeRange = `${slot.label} - ${endLabel}`;
    
    const context = this.historicalScansResponse?.contextName || this.contextName || 'Normale';
    const isNormale = context.toLowerCase() === 'normale';
    const todayFormatted = this.getTodayInEstablishmentTimezone();

    // Récupérer le point correspondant au créneau
    const statsPoint = this.historicalScansResponse?.points?.find((p: any) => p.time === slot.label);
    
    const periods = (this.historicalScansResponse?.periods || []).map((p: HistoricalPeriod) => {
      const periodAvg = statsPoint?.periodAvgs?.find((pa: any) => pa.periodId === p.id);
      return {
        ...p,
        name: isNormale ? 'Période courante' : p.name,
        startDate: this.formatDate(p.startDate),
        endDate: isNormale ? todayFormatted : this.formatDate(p.endDate),
        // 1 décimale (pas Math.round à l'entier) : la courbe est tracée avec la valeur
        // brute (yHistoricalScans), un arrondi à l'entier affichait "0" dans le tooltip
        // alors que la courbe montait visiblement pour toute valeur entre 0 et 0.5.
        avgScans: periodAvg ? Math.round(periodAvg.avgScans * 10) / 10 : 0
      };
    });

    this.cachedTooltipData = {
      time: `${dayName} ${timeRange}`,
      queueName: this.queueName || 'File d\'attente',
      context,
      scansMoyen: Math.round(scansMoyen * 10) / 10,
      periods
    };
    this.cachedTooltipIndex = index;
    
    return this.cachedTooltipData;
  }

  private getTodayInEstablishmentTimezone(): string {
    const tz = this.timezone.getEstablishmentTimezone();
    const now = new Date();
    const year = parseInt(formatInTimeZone(now, tz, 'yyyy'));
    const month = parseInt(formatInTimeZone(now, tz, 'MM'));
    const day = parseInt(formatInTimeZone(now, tz, 'dd'));
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lowThreshold']) this.lowTh.set(this.lowThreshold);
    if (changes['highThreshold']) this.highTh.set(this.highThreshold);

    if (changes['maxY']) {
      this.rebuildScaleFromData();
    }

    if (changes['customData']) {
      if (this.customData && Array.isArray(this.customData) && this.customData.length > 0) {
        this.points.set(this.customData);
        this.rebuildTimeSlots();
        this.loading.set(false);
      } else {
        this.points.set([]);
        this.rebuildTimeSlots();
        this.loading.set(false);
      }
      return;
    }

    if (changes['baselineData']) {
      this.rebuildBaselineSlots();
    }

    if (changes['queueId'] || changes['horizonMinutes'] || changes['fullDay'] || changes['dayOfWeek'] || changes['selectedPeriod']) {
      if (this.customData === undefined) {
        this.updating.set(true);
        this.restart$.next();
      }
      // Recharger les scans historiques uniquement si nécessaire
      if (this.loadHistoricalScans && (changes['queueId'] || changes['dayOfWeek'] || changes['selectedPeriod'])) {
        this.loadHistoricalScansData();
      }
      if (this.loadSensorFlow && (changes['queueId'] || changes['dayOfWeek'])) {
        this._sensorFlowInitialized = true;
        this.loadSensorFlowData();
      }
    }
  }

  ngOnInit(): void {
    this.lowTh.set(this.lowThreshold);
    this.highTh.set(this.highThreshold);

    // Si customData est défini (même vide), ne pas faire d'appels API
    if (this.customData !== undefined) {
      return;
    }

    // ngOnChanges s'exécute avant ngOnInit — éviter le double appel HTTP
    if (this.loadSensorFlow && !this._sensorFlowInitialized) {
      this.loadSensorFlowData();
    }

    this.sub = this.restart$.pipe(
      debounceTime(50),
      startWith(void 0),
      switchMap(() => interval(60000).pipe(startWith(0)))
    ).pipe(
      switchMap(() => {
        if (!this.queueId) return of<ForecastPoint[]>([]);

        const cacheKey = this.forecastCacheKey() + (this.loadBaseline ? ':baseline' : '');
        const cached = this.forecastCache.get(cacheKey);
        if (cached && (Date.now() - cached.fetchedAt) < this.FORECAST_CACHE_TTL_MS) {
          this.fullApiResponse.set(cached.fullResponse);
          if (cached.baseline) {
            this.baselineData = cached.baseline.points || [];
            this.rebuildBaselineSlots();
          }
          return of(cached.points);
        }

        if (this.fullDay) {
          const forecast$ = this.api.getDay(this.queueId, undefined, this.dayOfWeek);
          const baseline$ = this.loadBaseline ? this.api.getDayBaseline(this.queueId, undefined, this.dayOfWeek) : of(null);

          return combineLatest([forecast$, baseline$]).pipe(map(([f, b]) => {
            this.fullApiResponse.set(f);
            if (b) {
              this.baselineData = b.points || [];
              this.rebuildBaselineSlots();
            }
            const points = f.points ?? [];
            this.forecastCache.set(cacheKey, { points, fullResponse: f, baseline: b, fetchedAt: Date.now() });
            return points;
          }));
        }
        return this.api.get(this.queueId, this.horizonMinutes).pipe(map(f => {
          this.fullApiResponse.set(f);
          const points = f.points ?? [];
          this.forecastCache.set(cacheKey, { points, fullResponse: f, baseline: null, fetchedAt: Date.now() });
          return points;
        }));
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (pts) => {
        this.points.set(pts);
        this.rebuildTimeSlots();
        this.loading.set(false);
        setTimeout(() => this.updating.set(false), 100);
      },
      error: () => {
        this.points.set([]);
        this.rebuildTimeSlots();
        this.loading.set(false);
        setTimeout(() => this.updating.set(false), 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sub?.unsubscribe();
  }

  color(m: number): string {
    if (m === 1) return '#e5e7eb'; // Gris pour mini barre (0 minute réel)
    if (m <= this.lowTh()) return this.barColorLow;
    if (m <= this.highTh()) return this.barColorMid;
    return this.barColorHigh;
  }

  getBarClass(index: number, slotData: any): string {
    const classes = [];

    if (this.isCurrentTimeSlot(index) && slotData.minutes > 0) {
      classes.push('current-time-bar');
    }

    if (!slotData.open) {
      classes.push('closed-bar');
    }

    return classes.join(' ');
  }

  isEstablishmentClosed(): boolean {
    const pts = this.points();
    return Array.isArray(pts) && pts.length === 0;
  }

  // (compat éventuelle)
  heightPct(m: number): number {
    const v = Math.max(0, Math.min(60, m));
    return (v / 60) * 100;
  }
}
